import React, { useState, useRef, useEffect } from "react";
import Spinner from "../Spinner/Spinner"; // Assuming you have this component

import Autocomplete from "../Elements/Autocomplete";
import useCRUD from "../../hooks/useCRUD";
import { formatPhoneNumberForSms } from "../../utils/phoneNumberUtils";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckDouble,
  faUsers,
  faCircleCheck,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";

const DriverCard = ({
  driver,
  rides,
  onSendNotification,
  isLoading,
  allParticipants,
  onManagePickups,
  assignedLoad,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const cardRef = useRef(null); // Reference for the entire card
  const [messageForDriver, setMessageForDriver] = useState("");
  const [showPickupGuide, setShowPickupGuide] = useState(
    Boolean(driver.showPickupPageOnboarding),
  );

  const [isManaging, setIsManaging] = useState(false);
  const [isManagingMessage, setIsManagingMessage] = useState(false);
  const [isUpdatingPickupGuide, setIsUpdatingPickupGuide] = useState(false);

  const { updateItem } = useCRUD("/api/event-participations"); // Adjust with actual API path

  const toggleMenu = () => setShowMenu(!showMenu);

  const handleMessageChange = (e) => {
    setMessageForDriver(e.target.value);
  };

  const handleSendMessage = () => {
    setMessageForDriver(driver.messageFromAdmin);
    setIsManagingMessage(true);
    setShowMenu(false);
  };

  const handleSubmitMessage = async () => {
    if (!messageForDriver) return; // Prevent empty messages
    // Implement your API call logic here
    await updateItem(driver.id, { messageFromAdmin: messageForDriver });
    setMessageForDriver(""); // Clear the message input after submission
    alert("Message added."); // Provide feedback to the user
    setIsManagingMessage(false);
  };

  const updatePickupGuideSetting = async (
    shouldShowGuide,
    forceReset = false,
  ) => {
    const previousShowPickupGuide = showPickupGuide;
    setShowPickupGuide(shouldShowGuide);
    setIsUpdatingPickupGuide(true);

    if (forceReset) {
      await updateItem(driver.id, {
        showPickupPageOnboarding: false,
      });
    }

    const response = await updateItem(driver.id, {
      showPickupPageOnboarding: shouldShowGuide,
    });

    if (!response) {
      setShowPickupGuide(previousShowPickupGuide);
      alert("Unable to update pickup page guide setting.");
    }

    setIsUpdatingPickupGuide(false);
  };

  const handlePickupGuideMenuAction = () => {
    const shouldShowGuide =
      !showPickupGuide || Boolean(driver.pickupPageOnboardingViewedAt);
    const shouldResetViewedAt =
      showPickupGuide && Boolean(driver.pickupPageOnboardingViewedAt);

    setShowMenu(false);
    updatePickupGuideSetting(shouldShowGuide, shouldResetViewedAt);
  };

  const pickupGuideMenuLabel = isUpdatingPickupGuide
    ? "Updating pickup guide..."
    : showPickupGuide && driver.pickupPageOnboardingViewedAt
      ? "Show Pickup Guide Again"
      : showPickupGuide
        ? "Disable Pickup Guide"
        : "Enable Pickup Guide";

  useEffect(() => {
    setShowPickupGuide(Boolean(driver.showPickupPageOnboarding));
  }, [driver.showPickupPageOnboarding]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the entire card
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        if (isManaging) {
          setIsManaging(false); // Reset isManaging only if the click is outside the card
        }
        setShowMenu(false); // You may still want to close the menu
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isManaging]);

  const handleAddParticipant = (participantId) => {
    // Function to add participant to driver's list
    onManagePickups(driver.participantId, participantId, "add");
  };

  const handleRemoveParticipant = (participantId) => {
    // Function to remove participant from driver's list
    console.log("Removing participant", participantId);
    console.log("Driver ID:", driver);
    onManagePickups(driver.id, participantId, "remove");
  };

  const handleMenuSelection = (action) => {
    // Perform action based on the selection
    if (action === "sendNotification") {
      onSendNotification(driver.id);
    } else if (action === "managePickups") {
      setIsManaging(!isManaging);
    }
    // Close the menu
    setShowMenu(false);
  };

  // Filter participants who need a ride and are not assigned yet
  const availableParticipants = allParticipants?.filter(
    (p) => p.needRide === "Yes" && !p.assignedDriverId,
  );

  const capacity = Number(driver.canTake || 0);
  const assignedSeats =
    assignedLoad ??
    rides.reduce((total, participant) => total + (participant.noOfPpl || 1), 0);
  const remainingSeats = Math.max(0, capacity - assignedSeats);

  return (
    <div
      ref={cardRef}
      className="card rounded-xl border border-gray-100 bg-white shadow-lg"
    >
      {" "}
      {!driver.confirmedPickupAssignment && driver.pickupNotificationSent && (
        <div
          className="tooltip"
          data-tip="Pickup assignments have been sent to driver."
        >
          <div className="absolute top-0 left-0 bg-blue-500 text-white text-center py-0.5 px-2 text-xs rounded-r-lg">
            Notification Sent
          </div>
        </div>
      )}
      {!driver.confirmedPickupAssignment && !driver.pickupNotificationSent && (
        <div
          className="tooltip"
          data-tip="Hasn't received a notification yet about pickup assignments. Click card menu to send individual notification"
        >
          <div className="absolute top-0 left-0 bg-yellow-500 text-white text-center py-0.5 px-2 text-xs rounded-r-lg">
            Notification Pending
          </div>
        </div>
      )}
      {driver.confirmedPickupAssignment && (
        <div
          className="tooltip"
          data-tip="Pickup assignments have been confirmed by driver."
        >
          <div className="absolute top-0 left-0 bg-green-500 text-white text-center py-0.5 px-2 text-xs rounded-r-lg">
            Confirmed
          </div>
        </div>
      )}
      {/* Use the cardRef here */}
      <div className="card-body">
        <h2 className="card-title">
          <i className="fas fa-car-side"></i> &nbsp;
          <a href={`sms:${formatPhoneNumberForSms(driver.phone)}`}>
            {driver.name}
          </a>
          {driver.messageFromAdmin && (
            <span
              className="tooltip ml-2 text-[#004a7e]"
              data-tip={`Message for driver: ${driver.messageFromAdmin}`}
            >
              <FontAwesomeIcon icon={faEnvelope} />
            </span>
          )}
          {driver.urlLastViewedAt && (
            <span
              className="tooltip ml-1"
              data-tip={new Date(driver.urlLastViewedAt).toLocaleString()}
            >
              <FontAwesomeIcon icon={faCircleCheck} className="text-blue-500" />
            </span>
          )}
          <button
            onClick={toggleMenu}
            className="btn btn-square btn-ghost absolute right-2 top-2"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16m-7 6h7"
              ></path>
            </svg>
          </button>
          {showMenu && (
            <div
              ref={menuRef}
              className="absolute right-0 mt-12 w-64 bg-white rounded-md shadow-xl z-10"
            >
              <ul className=" list-none text-sm py-1 text-gray-700">
                <li>
                  <button
                    onClick={() => handleMenuSelection("sendNotification")}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    Send Notification
                  </button>
                </li>
                <li>
                  <button
                    onClick={() =>
                      window.open("/r/" + driver.uniqueCode, "_blank")
                    }
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    Pickup Link
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => handleMenuSelection("managePickups")}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    Manage Pickups
                  </button>
                </li>
                <li>
                  <button
                    onClick={handleSendMessage}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    Add Message For Driver
                  </button>
                </li>
                <li className="border-t border-gray-100">
                  <button
                    onClick={handlePickupGuideMenuAction}
                    disabled={isUpdatingPickupGuide}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {pickupGuideMenuLabel}
                    {driver.pickupPageOnboardingViewedAt && showPickupGuide && (
                      <span className="block text-xs text-gray-500">
                        Already shown once
                      </span>
                    )}
                  </button>
                </li>
              </ul>
            </div>
          )}
        </h2>

        <div className="mt-2 inline-flex w-fit items-center gap-2 rounded-full border border-[#d7e8f4] bg-[#f3f8fc] px-3 py-1.5 text-xs font-bold text-[#004a7e]">
          <FontAwesomeIcon icon={faUsers} />
          <span>
            {assignedSeats}/{capacity} assigned
          </span>
          <span className="text-gray-300">•</span>
          <span
            className={remainingSeats > 0 ? "text-green-700" : "text-red-600"}
          >
            {remainingSeats} open
          </span>
        </div>

        {isManagingMessage && (
          <div className="mt-4">
            <textarea
              value={messageForDriver}
              onChange={handleMessageChange}
              className="textarea textarea-bordered w-full"
              placeholder="Type your message here.This will appear on driver's pickup page."
            ></textarea>
            <button
              onClick={handleSubmitMessage}
              className="btn btn-primary mt-2"
            >
              Submit Message
            </button>
          </div>
        )}
        {isManaging && (
          <>
            <Autocomplete
              options={availableParticipants}
              onSelect={handleAddParticipant}
              placeholder="Add participant..."
            />
            {rides.map((participant) => (
              <div
                key={participant.participantId}
                className="flex justify-between items-center"
              >
                <span>
                  {participant.name}
                  {participant.urlLastViewedAt && (
                    <span
                      className="tooltip ml-1"
                      data-tip={new Date(
                        participant.urlLastViewedAt,
                      ).toLocaleString()}
                    >
                      <FontAwesomeIcon
                        icon={faCircleCheck}
                        className="text-blue-500"
                      />
                    </span>
                  )}{" "}
                  {participant.noOfPpl > 1 ? `(${participant.noOfPpl})` : ""}-{" "}
                  {participant.rideLocation}
                </span>
                <button
                  className="btn btn-error  text-white btn-xs"
                  onClick={() => handleRemoveParticipant(participant)}
                >
                  Remove
                </button>
              </div>
            ))}
          </>
        )}
        {!isManaging && (
          <div className="mt-3 space-y-1.5">
            {rides.map((participant) => (
              <div
                key={participant.participantId}
                className="rounded-md border border-gray-100 bg-gray-50 px-2.5 py-2 text-sm"
              >
                <span className="block truncate font-semibold text-gray-900">
                  <FontAwesomeIcon
                    icon={faUsers}
                    className="mr-1.5 text-[#004a7e]"
                  />
                  {participant.name}
                  {participant.urlLastViewedAt && (
                    <span
                      className="tooltip ml-1"
                      data-tip={new Date(
                        participant.urlLastViewedAt,
                      ).toLocaleString()}
                    >
                      <FontAwesomeIcon
                        icon={faCircleCheck}
                        className="text-blue-500"
                      />
                    </span>
                  )}
                  {participant.noOfPpl > 1 ? ` (${participant.noOfPpl})` : ""}
                  {participant.pickupNotificationSent && (
                    <div className="tooltip" data-tip="Driver Assignment sent">
                      <FontAwesomeIcon
                        icon={faCheckDouble}
                        className="text-blue-500 ml-2"
                      />
                    </div>
                  )}
                </span>
                <span className="mt-0.5 block truncate text-xs text-gray-500">
                  {participant.rideLocation}
                </span>
              </div>
            ))}
          </div>
        )}
        {isLoading && <Spinner />}
      </div>
    </div>
  );
};

export default DriverCard;
